/**
 * dev-console.ts — MFix7.4 (2026-07-23).
 *
 * Developer overlay toggled by the `~` (Backquote) key. Provides shortcut
 * buttons for:
 *  - +1000 of each of the 7 resources (Gold, Iron, Wood, Horses, Food,
 *    Supplies, Learning) on the player's ledger.
 *  - Create Army with one cohort of each v1 combat type (Swordsman,
 *    Spearman, Archer, Light Cavalry, Shock Cavalry, Catapult) placed
 *    at the player's capital cell.
 *
 * The overlay directly mutates `SimDriver.getState()` return values —
 * these are references into the sim state, so mutations persist. This is
 * intentional for a dev tool; NEVER use in production gameplay paths.
 *
 * Hidden by default. Style matches the rest of the menu overlays
 * (parchment on dark).
 */

import type { FactionId, GameState } from '../../sim/state';
import type { SimDriver } from '../sim-driver';
import {
  createArmy,
  createCommander,
  toArmyId,
  toCohortId,
  toCommanderId,
} from '../../sim/state';
import type { Army, Cohort, Commander } from '../../sim/state';
import { loadCohorts } from '../../data/cohorts';
import { countCellStates } from '../../sim/visibility';
import { getRenderFlags, toggleRevealAllCells } from '../render-flags';

const DEV_COHORT_TYPES: readonly string[] = [
  'Swordsman',
  'Spearman',
  'Archer',
  'Light Cavalry',
  'Shock Cavalry',
  'Catapult',
];

const RESOURCE_KEYS: readonly ('gold' | 'iron' | 'wood' | 'horses' | 'supplies' | 'learning')[] = [
  'gold',
  'iron',
  'wood',
  'horses',
  'supplies',
  'learning',
];

export interface DevConsoleHandle {
  destroy(): void;
}

export function mountDevConsole(args: {
  root: HTMLElement;
  driver: SimDriver;
  playerFactionId: FactionId | null;
}): DevConsoleHandle {
  const { root, driver, playerFactionId } = args;

  const panel = document.createElement('div');
  panel.className = 'su-dev-console';
  panel.style.position = 'fixed';
  panel.style.top = '50%';
  panel.style.left = '50%';
  panel.style.transform = 'translate(-50%, -50%)';
  panel.style.background = 'rgba(20, 16, 10, 0.96)';
  panel.style.border = '2px solid #f4d47a';
  panel.style.borderRadius = '4px';
  panel.style.padding = '14px 16px';
  panel.style.zIndex = '3000';
  panel.style.color = '#f5eacf';
  panel.style.fontFamily = '"Trajan Pro", "Cinzel", "Times New Roman", serif';
  panel.style.minWidth = '360px';
  panel.style.display = 'none';
  panel.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.6)';

  const header = document.createElement('div');
  header.style.fontWeight = 'bold';
  header.style.fontSize = '14px';
  header.style.marginBottom = '10px';
  header.style.paddingBottom = '6px';
  header.style.borderBottom = '1px solid #4a3820';
  header.textContent = 'Dev Tools — press ~ to close';
  panel.appendChild(header);

  const resourceSection = document.createElement('div');
  const resourceLabel = document.createElement('div');
  resourceLabel.style.fontSize = '12px';
  resourceLabel.style.opacity = '0.85';
  resourceLabel.style.marginBottom = '4px';
  resourceLabel.textContent = 'Resources — +1000';
  resourceSection.appendChild(resourceLabel);

  const resourceRow = document.createElement('div');
  resourceRow.style.display = 'flex';
  resourceRow.style.flexWrap = 'wrap';
  resourceRow.style.gap = '4px';
  resourceRow.style.marginBottom = '10px';
  for (const key of RESOURCE_KEYS) {
    const btn = document.createElement('button');
    btn.textContent = `+1000 ${cap(key)}`;
    styleButton(btn);
    btn.onclick = () => {
      if (playerFactionId === null) return;
      const state = driver.getState();
      const faction = state.factions.get(playerFactionId);
      if (!faction) return;
      faction.resources[key] += 1000;
    };
    resourceRow.appendChild(btn);
  }
  resourceSection.appendChild(resourceRow);
  panel.appendChild(resourceSection);

  const armySection = document.createElement('div');
  const armyLabel = document.createElement('div');
  armyLabel.style.fontSize = '12px';
  armyLabel.style.opacity = '0.85';
  armyLabel.style.marginBottom = '4px';
  armyLabel.textContent = 'Army';
  armySection.appendChild(armyLabel);

  const armyBtn = document.createElement('button');
  armyBtn.textContent = 'Create test Army (one cohort of each type)';
  styleButton(armyBtn);
  armyBtn.style.width = '100%';
  armyBtn.onclick = () => {
    if (playerFactionId === null) return;
    spawnTestArmy(driver.getState(), playerFactionId);
  };
  armySection.appendChild(armyBtn);
  panel.appendChild(armySection);

  // v0M5.1 (2026-07-25) — Vision override section.
  //   - Reveal all cells: forces the fog renderer (v0M5.2+) to draw every
  //     cell at state 4 (visible + detection) regardless of the sim
  //     visibility map. Useful for playtest + debug.
  //   - Print visibility stats: logs the player faction's cell counts
  //     (unexplored / explored / visible / detected) to console.
  const visionSection = document.createElement('div');
  visionSection.style.marginTop = '10px';
  const visionLabel = document.createElement('div');
  visionLabel.style.fontSize = '12px';
  visionLabel.style.opacity = '0.85';
  visionLabel.style.marginBottom = '4px';
  visionLabel.textContent = 'Vision';
  visionSection.appendChild(visionLabel);

  const visionRow = document.createElement('div');
  visionRow.style.display = 'flex';
  visionRow.style.gap = '4px';
  visionRow.style.marginBottom = '4px';

  const revealBtn = document.createElement('button');
  const refreshRevealLabel = (): void => {
    revealBtn.textContent = `Reveal all cells: ${getRenderFlags().revealAllCells ? 'ON' : 'OFF'}`;
  };
  refreshRevealLabel();
  styleButton(revealBtn);
  revealBtn.style.flex = '1';
  revealBtn.onclick = () => {
    toggleRevealAllCells();
    refreshRevealLabel();
  };
  visionRow.appendChild(revealBtn);

  const printBtn = document.createElement('button');
  printBtn.textContent = 'Print visibility stats';
  styleButton(printBtn);
  printBtn.style.flex = '1';
  printBtn.onclick = () => {
    if (playerFactionId === null) {
      console.info('[dev-console] no player faction — visibility stats unavailable');
      return;
    }
    const state = driver.getState();
    const vis = state.visibility?.get(playerFactionId);
    if (!vis) {
      console.info(`[dev-console] player faction ${playerFactionId} has no visibility map (v0M5.1 system not yet run?)`);
      return;
    }
    const counts = countCellStates(vis);
    const total = counts.unexplored + counts.explored + counts.visible + counts.detected;
    const pct = (n: number): string => `${((n / total) * 100).toFixed(1)}%`;
    console.info(
      `[dev-console] faction ${playerFactionId} visibility (${vis.width}×${vis.height}, ${total} cells):`,
      `\n  1 Unexplored: ${counts.unexplored} (${pct(counts.unexplored)})`,
      `\n  2 Explored:   ${counts.explored} (${pct(counts.explored)})`,
      `\n  3 Visible:    ${counts.visible} (${pct(counts.visible)})`,
      `\n  4 Detected:   ${counts.detected} (${pct(counts.detected)})`,
    );
  };
  visionRow.appendChild(printBtn);
  visionSection.appendChild(visionRow);
  panel.appendChild(visionSection);

  root.appendChild(panel);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === '`' || e.key === '~') {
      panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
      e.preventDefault();
    }
  };
  window.addEventListener('keydown', onKey);

  return {
    destroy() {
      window.removeEventListener('keydown', onKey);
      panel.remove();
    },
  };
}

function styleButton(btn: HTMLButtonElement): void {
  btn.style.background = '#3a2c18';
  btn.style.color = '#f5eacf';
  btn.style.border = '1px solid #6a5238';
  btn.style.borderRadius = '3px';
  btn.style.padding = '5px 8px';
  btn.style.fontSize = '11px';
  btn.style.fontFamily = 'inherit';
  btn.style.cursor = 'pointer';
  btn.onmouseenter = () => { btn.style.background = '#4d3a20'; };
  btn.onmouseleave = () => { btn.style.background = '#3a2c18'; };
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Spawn a fresh Army for `playerFactionId` at their capital city cell,
 * pre-loaded with one cohort of each entry in DEV_COHORT_TYPES.
 * Silently no-ops if the faction lacks a capital or a Cohorts spec is
 * missing.
 */
function spawnTestArmy(state: GameState, playerFactionId: FactionId): void {
  const faction = state.factions.get(playerFactionId);
  if (!faction) return;
  const capitalSettlementId = faction.capitalSettlementId;
  if (capitalSettlementId === undefined) return;
  const capital = state.settlements.get(capitalSettlementId);
  if (!capital) return;

  // Commander first.
  const commanderId = toCommanderId(state.nextCommanderId++);
  const commander: Commander = createCommander(commanderId);
  state.commanders.set(commanderId, commander);

  // Army at capital cell.
  const armyId = toArmyId(state.nextArmyId++);
  const army: Army = createArmy(
    armyId,
    playerFactionId,
    commanderId,
    { x: capital.cell.x + 0.5, y: capital.cell.y + 0.5 },
  );
  state.armies.set(armyId, army);

  // Cohorts.
  const COHORTS = loadCohorts();
  for (const cohortType of DEV_COHORT_TYPES) {
    const spec = COHORTS[cohortType];
    if (!spec) continue;
    const cohortId = toCohortId(state.nextCohortId++);
    const cohort: Cohort = {
      id: cohortId,
      factionId: playerFactionId,
      armyId,
      settlementId: null,
      type: cohortType,
      aliveFigureCount: spec.figureCount,
      stamina: 100,
      morale: 100,
      xp: 0,
      cohortRace: 'Human',
    };
    state.cohorts.set(cohortId, cohort);
    army.cohortIds.push(cohortId);
  }
}
